/**
 * Inline cell.yaml editor for a v0.6 project.
 *
 * Shown on the project page (ProjectDetail). Reads
 * ``GET /api/projects/{id}/cell`` on mount, keeps the full
 * ProjectCellPayload in local state, and PATCHes the whole document
 * back on Save. We send the whole doc rather than a partial patch
 * because the backend validator wants a complete ProjectCellPayload
 * — simpler, and the dicts stay small.
 *
 * No bidirectional dirty tracking per field; one global dirty flag
 * enables the Save button. If a user navigates away mid-edit we lose
 * their changes — acceptable for v0.6 Phase 2B. Phase 2C can add a
 * dockview-aware beforeunload guard.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Callout,
  Card,
  Divider,
  FormGroup,
  NumericInput,
  Switch,
} from '@blueprintjs/core'

import {
  getProjectCell,
  patchProjectCell,
  type ProjectCellPayload,
} from '../api'


export function ProjectCellEditor({ projectId }: { projectId: string }) {
  const [cell, setCell] = useState<ProjectCellPayload | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getProjectCell(projectId).then(setCell).catch((ex) => setErr(String(ex)))
  }, [projectId])

  const save = async () => {
    if (!cell) return
    setSaving(true)
    setErr(null)
    try {
      const updated = await patchProjectCell(projectId, cell)
      setCell(updated)
      setDirty(false)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    } finally {
      setSaving(false)
    }
  }

  const mutate = (updater: (c: ProjectCellPayload) => ProjectCellPayload) => {
    if (!cell) return
    setCell(updater(cell))
    setDirty(true)
  }

  if (err && !cell) return <Callout intent="danger" icon="error">{err}</Callout>
  if (!cell) return <div style={{ padding: 12 }}>Loading cell…</div>

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Cell</h3>
        <Button
          icon="floppy-disk"
          intent={dirty ? 'primary' : 'none'}
          disabled={!dirty || saving}
          loading={saving}
          onClick={save}
        >
          Save
        </Button>
      </div>
      {err && <Callout intent="danger" icon="error" style={{ marginTop: 8 }}>{err}</Callout>}

      <Divider />

      <h4>Robot</h4>
      <FormGroup label="Robot model" helperText="Picks the kinematics module and the mesh set.">
        <input
          className="bp5-input bp5-fill"
          value={cell.robot.robot_id}
          onChange={(e) =>
            mutate((c) => ({ ...c, robot: { ...c.robot, robot_id: e.target.value } }))}
        />
      </FormGroup>
      <FormGroup label="Tool length (m)" helperText="Flange face to the weld-tip. Used in IK to project the TCP.">
        <NumericInput
          minorStepSize={0.001}
          stepSize={0.005}
          majorStepSize={0.05}
          value={cell.robot.tool_length_m}
          onValueChange={(v) =>
            mutate((c) => ({ ...c, robot: { ...c.robot, tool_length_m: v || 0 } }))}
        />
      </FormGroup>
      <Vec3Field
        label="Base position (mm)"
        value={cell.robot.position_mm}
        onChange={(v) => mutate((c) => ({ ...c, robot: { ...c.robot, position_mm: v } }))}
      />
      <Vec3Field
        label="Base orientation (deg)"
        helper="XYZ intrinsic Euler. Leave at (0, 0, 0) unless the base is tilted."
        value={cell.robot.orientation_deg}
        onChange={(v) => mutate((c) => ({ ...c, robot: { ...c.robot, orientation_deg: v } }))}
      />

      <Divider />

      <h4>Positioner</h4>
      <FormGroup label="Model id">
        <input
          className="bp5-input bp5-fill"
          value={cell.positioner.model_id}
          onChange={(e) =>
            mutate((c) => ({ ...c, positioner: { ...c.positioner, model_id: e.target.value } }))}
        />
      </FormGroup>
      <FormGroup label="Mech unit name" helperText="Must match the external-axis task in your RobotStudio VC (e.g. STN1).">
        <input
          className="bp5-input bp5-fill"
          value={cell.positioner.mech_unit_name}
          onChange={(e) =>
            mutate((c) => ({ ...c, positioner: { ...c.positioner, mech_unit_name: e.target.value } }))}
        />
      </FormGroup>
      <Vec3Field
        label="Base position (mm, in robot RCF)"
        helper="Where the positioner base sits relative to the robot base. Typical ~1.6 m in front."
        value={cell.positioner.base_position_mm}
        onChange={(v) =>
          mutate((c) => ({ ...c, positioner: { ...c.positioner, base_position_mm: v } }))}
      />
      <Vec3Field
        label="Base orientation (deg)"
        helper="XYZ intrinsic Euler. 90° yaw rotates the shaft from robot +X to +Y."
        value={cell.positioner.base_orientation_deg}
        onChange={(v) =>
          mutate((c) => ({ ...c, positioner: { ...c.positioner, base_orientation_deg: v } }))}
      />
      <Vec3Field
        label="First axis (in base frame)"
        helper="Direction of the first rotary axis. (1, 0, 0) horizontal wheel; (0, 0, 1) lazy susan."
        value={cell.positioner.first_axis_in_base}
        onChange={(v) =>
          mutate((c) => ({ ...c, positioner: { ...c.positioner, first_axis_in_base: v } }))}
      />
      <Vec3Field
        label="Faceplate offset (mm, in base frame)"
        helper="Faceplate centre relative to positioner base. Typical (0, 0, 1100) for an A-250 D1000."
        value={cell.positioner.faceplate_offset_mm}
        onChange={(v) =>
          mutate((c) => ({ ...c, positioner: { ...c.positioner, faceplate_offset_mm: v } }))}
      />
      <FormGroup label="RAPID external axis" helperText="On = emit ActUnit/DeactUnit and wire the positioner as an external axis in wobjdata.">
        <Switch
          checked={cell.positioner.activate_in_proc}
          label={cell.positioner.activate_in_proc ? 'Active' : 'Off'}
          onChange={(e) => {
            const checked = (e.target as HTMLInputElement).checked
            mutate((c) => ({ ...c, positioner: { ...c.positioner, activate_in_proc: checked } }))
          }}
        />
      </FormGroup>

      <Divider />

      <h4>Tilt axis (2-axis positioner)</h4>
      <FormGroup
        label="Tilt mech unit"
        helperText={
          'Blank = single-axis positioner (default). Set e.g. STN2 to ' +
          'declare a second IRC5 external axis — the RAPID emitter will ' +
          'write eax_b in every jointtarget and add ActUnit/DeactUnit ' +
          'around main. Only for drop-center or K-type positioners that ' +
          'actually have a tilt drive.'
        }
      >
        <input
          className="bp5-input bp5-fill"
          placeholder="(blank for single-axis)"
          value={cell.positioner.tilt_mech_unit_name ?? ''}
          onChange={(e) =>
            mutate((c) => ({ ...c, positioner: { ...c.positioner, tilt_mech_unit_name: e.target.value } }))}
        />
      </FormGroup>
      <Vec3Field
        label="Tilt axis (in base frame)"
        helper="Direction of the tilt rotation axis in the positioner's base frame. Default (0, 1, 0) tilts a horizontal-shaft positioner up/down."
        value={cell.positioner.tilt_axis_in_base ?? [0, 1, 0]}
        onChange={(v) =>
          mutate((c) => ({ ...c, positioner: { ...c.positioner, tilt_axis_in_base: v } }))}
      />
      <Vec3Field
        label="Tilt housing offset (mm, in base)"
        helper="Where the spin axis emerges from the tilt housing. (0, 0, 0) = tilt and spin share one pivot."
        value={cell.positioner.tilt_housing_offset_mm ?? [0, 0, 0]}
        onChange={(v) =>
          mutate((c) => ({ ...c, positioner: { ...c.positioner, tilt_housing_offset_mm: v } }))}
      />

      <Divider />

      <h4>Work object</h4>
      <Vec3Field
        label="Offset on faceplate (mm)"
        helper="Centre of the welded piece in the faceplate frame."
        value={cell.work_object.offset_mm}
        onChange={(v) =>
          mutate((c) => ({ ...c, work_object: { ...c.work_object, offset_mm: v } }))}
      />
      <Vec3Field
        label="Orientation on faceplate (deg)"
        value={cell.work_object.orientation_deg}
        onChange={(v) =>
          mutate((c) => ({ ...c, work_object: { ...c.work_object, orientation_deg: v } }))}
      />
      <Vec3Field
        label="Worm axis (in faceplate frame)"
        helper="Axis of the worm's own long dimension. Default (0, 0, 1) = along faceplate normal."
        value={cell.work_object.worm_axis_in_faceplate}
        onChange={(v) =>
          mutate((c) => ({ ...c, work_object: { ...c.work_object, worm_axis_in_faceplate: v } }))}
      />

      <Divider />

      <h4>Tool</h4>
      <FormGroup label="Active tool id" helperText="Resolved against the tool library at run time.">
        <input
          className="bp5-input bp5-fill"
          value={cell.active_tool_id ?? ''}
          onChange={(e) => mutate((c) => ({ ...c, active_tool_id: e.target.value || null }))}
        />
      </FormGroup>
    </Card>
  )
}


function Vec3Field({
  label, helper, value, onChange,
}: {
  label: string
  helper?: string
  value: number[]
  onChange: (v: number[]) => void
}) {
  const v = [value[0] ?? 0, value[1] ?? 0, value[2] ?? 0]
  return (
    <FormGroup label={label} helperText={helper}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {v.map((n, i) => (
          <NumericInput
            key={i}
            value={n}
            onValueChange={(nv) => {
              const next = [...v]
              next[i] = isNaN(nv) ? 0 : nv
              onChange(next)
            }}
          />
        ))}
      </div>
    </FormGroup>
  )
}
